/**
 * SectionLibraryModal Component
 *
 * Modal/drawer for browsing and adding sections from the Section Library.
 * Allows users to search, filter, and preview sections before adding them to templates.
 */

import React, { useMemo } from 'react';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useSectionLibrary } from '../hooks/useSectionLibrary';
import type { SectionLibraryWithMeta } from '../types';

import { SectionLibraryCard } from './SectionLibraryCard';

interface SectionLibraryModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
  /** Callback when user adds a section to template */
  onAddSection: (section: SectionLibraryWithMeta) => void;
}

export const SectionLibraryModal: React.FC<SectionLibraryModalProps> = ({
  isOpen,
  onClose,
  onAddSection,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    sections,
    isLoading,
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
  } = useSectionLibrary();

  // Determine which sections to display
  const displaySections = useMemo(() => {
    if (searchResults !== null) {
      return searchResults;
    }
    return sections;
  }, [sections, searchResults]);

  // Group sections by scope
  const groupedSections = useMemo(() => {
    const groups: {
      public: SectionLibraryWithMeta[];
      org: SectionLibraryWithMeta[];
      user: SectionLibraryWithMeta[];
    } = {
      public: [],
      org: [],
      user: [],
    };

    displaySections.forEach(section => {
      switch (section.section.owner_scope) {
        case 'public':
          groups.public.push(section);
          break;
        case 'org':
          groups.org.push(section);
          break;
        case 'user':
          groups.user.push(section);
          break;
      }
    });

    return groups;
  }, [displaySections]);

  const hasActiveSearch = searchTerm.length > 0;
  const hasResults = displaySections.length > 0;

  const handleAddSection = (section: SectionLibraryWithMeta) => {
    onAddSection(section);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? '100%' : '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Section Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and add pre-built sections to your template
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
          aria-label="Close library"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Search */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
          <InputBase
            fullWidth
            placeholder="Search sections by title, description, or content..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            inputProps={{
              'aria-label': 'Search sections',
            }}
            sx={{
              '& input::placeholder': {
                color: 'text.secondary',
                opacity: 0.8,
              },
            }}
          />
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
          }}
        >
          {/* Loading State */}
          {(isLoading || isSearching) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 8,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {/* Sections List */}
          {!isLoading && !isSearching && (
            <>
              {hasResults ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                  }}
                >
                  {/* Public Library Sections */}
                  {groupedSections.public.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        Public Library ({groupedSections.public.length})
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {groupedSections.public.map(section => (
                          <SectionLibraryCard
                            key={section.section.id}
                            section={section}
                            onAddToTemplate={handleAddSection}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Organization Sections */}
                  {groupedSections.org.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        Organization ({groupedSections.org.length})
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {groupedSections.org.map(section => (
                          <SectionLibraryCard
                            key={section.section.id}
                            section={section}
                            onAddToTemplate={handleAddSection}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* My Sections */}
                  {groupedSections.user.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        color="text.secondary"
                        sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        My Sections ({groupedSections.user.length})
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {groupedSections.user.map(section => (
                          <SectionLibraryCard
                            key={section.section.id}
                            section={section}
                            onAddToTemplate={handleAddSection}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                /* Empty State */
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                    No sections found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hasActiveSearch
                      ? 'Try adjusting your search query'
                      : 'No sections are available at this time'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

